import { NavigationPathsEnum } from "@utilities/enums";
import { useCategory, useInventory, useNavigation, useProduct } from "@utilities/hooks";
import { AddSupplyInputType, Category, CreateNewInventoryType, Product, UpdateProductFormType } from "@utilities/types";
import { useCallback, useEffect, useMemo, useState } from "react";

export const useController = () => {

    const { navigateTo } = useNavigation();
    const { 
        categories, onUpdateCategory,
        onGetAllOrganisationCategories,
    } = useCategory();
    const {
        productsPagination, products, 
        setProducts, onSupplyProduct,
        onGetAllOrganisationProductsByCategory,
        onUpdateProduct,
    } = useProduct();
    const { 
        invertories, 
        onCreateInventory, onGetAllOrganisationInvertories,
    } = useInventory();

    const [displayCategoryProducts, setDisplayCategoryProducts] = useState<boolean>(false);
    const [searchProduct, setSearchProduct] = useState<string>("");
    const [searchCategory, setSearchCategory] = useState<string>("");
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [isSupplyModalOpen, setIsSupplyModalOpen] = useState<boolean>(false);
    const [isInvetoryModalOpen, setIsInvetoryModalOpen] = useState<boolean>(false);
    const [isPricingEvolutionModalOpen, setIsPricingEvolutionModalOpen] = useState<boolean>(false);
    const [isViewProductDetailsModalOpen, setIsViewProductDetailsModalOpen] = useState<boolean>(false);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

    const filteredCategories = useMemo(()=> {
        if(!searchCategory) return categories;
        return categories.filter(category => category.name.toLowerCase().includes(searchCategory.toLowerCase()));
    }, [categories, searchCategory]);

    const filteredProducts = useMemo(()=> {
        if(!searchProduct) return products;
        return products.filter(product => product.name.toLowerCase().includes(searchProduct.toLowerCase()));
    }, [products, searchProduct]);

    useEffect(()=> {
        onGetAllOrganisationCategories();
        onGetAllOrganisationInvertories();
    }, []);

    const handleDisplayCategoryProducts = useCallback(async(category: Category)=> {
        setSelectedCategory(category);
        await onGetAllOrganisationProductsByCategory(category._id, 1, true);
        setDisplayCategoryProducts(true);
    }, [onGetAllOrganisationProductsByCategory]);

    const handleCategoryProductsBackClick = useCallback(() => {
        setDisplayCategoryProducts(false);
        setProducts([]);
    }, [setProducts]);

    const handleBackClick = useCallback(() => {
        navigateTo(NavigationPathsEnum.STOCKS)
    }, [navigateTo]);

    const handleAddCategoryOrProduct = useCallback(() => {
        navigateTo(NavigationPathsEnum.ADD_ARTICLE);
    }, [navigateTo]);

    const handleSupplyProduct = useCallback((product: Product) => {
        setSelectedProduct(product);
        setIsSupplyModalOpen(true);
    }, []);

    const handleSupplyModalClose = useCallback(() => {
        setIsSupplyModalOpen(false);
        setSelectedProduct(null);
    }, []);

    const handleCreateSupplyProduct = useCallback(async(newSupply: AddSupplyInputType)=> {
        if(!selectedProduct) return;
        await onSupplyProduct(selectedProduct._id!, newSupply);
        handleSupplyModalClose();
    }, [selectedProduct, onSupplyProduct, handleSupplyModalClose]);

    const handleInventoryProduct = useCallback((product: Product) => {
        setSelectedProduct(product);
        setIsInvetoryModalOpen(true);
    }, []);

    const handleInventoryModalClose = useCallback(() => {
        setIsInvetoryModalOpen(false);
        setSelectedProduct(null);
    }, []);

    const handleCreateInventoryProduct = useCallback(async(newInventory: Partial<CreateNewInventoryType>)=> {
        if(!selectedProduct) return;
        await onCreateInventory({
            ...newInventory,
            productID: selectedProduct._id!,
        });
        await onGetAllOrganisationProductsByCategory(selectedProduct._categoryID._id, 1, true);
        handleInventoryModalClose();
    }, [
        selectedProduct, onCreateInventory, 
        onGetAllOrganisationProductsByCategory, handleInventoryModalClose
    ]);

    const handleChangePage = useCallback(async(page: number) => {
        if(selectedCategory) {
            await onGetAllOrganisationProductsByCategory(selectedCategory._id, page, true);
        }
    }, [selectedCategory, onGetAllOrganisationProductsByCategory]);

    const handleUpdateCategory = useCallback(async(category: Partial<Category>)=> {
        await onUpdateCategory(category._id!, category);
    }, [onUpdateCategory]);

    const handleUpdateProduct = useCallback(async(productID: string, product: UpdateProductFormType)=> {
        await onUpdateProduct(productID, product);
        await onGetAllOrganisationProductsByCategory(selectedCategory!._id, 1, true);
    }, [selectedCategory, onGetAllOrganisationProductsByCategory, onUpdateProduct]);

    const handlePricingEvolution = useCallback((product: Product) => {
        setSelectedProduct(product);
        setIsPricingEvolutionModalOpen(true);
    }, []);

    const handlePricingEvolutionModalClose = useCallback(() => {
        setIsPricingEvolutionModalOpen(false);
        setSelectedProduct(null);
    }, []);

    const handleViewProductDetails = useCallback((product: Product) => {
        setSelectedProduct(product);
        setIsViewProductDetailsModalOpen(true);
    }, []);

    const handleViewProductDetailsModalClose = useCallback(() => {
        setIsViewProductDetailsModalOpen(false);
        setSelectedProduct(null);
    }, []);

    return {
        isPricingEvolutionModalOpen, isViewProductDetailsModalOpen,
        filteredCategories, isInvetoryModalOpen, productsPagination,
        filteredProducts, displayCategoryProducts, selectedCategory,
        selectedProduct, isSupplyModalOpen, invertories, categories,
        handleBackClick, handleDisplayCategoryProducts, handleUpdateCategory,
        handleCategoryProductsBackClick, handleCreateInventoryProduct,
        setSearchProduct, setSearchCategory, handleChangePage,
        handleAddCategoryOrProduct, handleSupplyProduct, handleUpdateProduct,
        handleSupplyModalClose, handleCreateSupplyProduct, handlePricingEvolutionModalClose,
        handleInventoryProduct, handleInventoryModalClose, handleViewProductDetailsModalClose,
        handleViewProductDetails, handlePricingEvolution,
    }
}
